import { Entry, EntryCollection } from 'contentful'
import removeMarkdown from 'remove-markdown'

import { IPage } from '../../../cea/contentful'
import { IEntry, ILink, isEntry } from './types'

/** Flattens a Contentful response by returning the array of entries returned */
export function flattenEntryResponse<T>(
  entries: EntryCollection<T>,
): Entry<T>[] {
  return entries.items
}

/** Flattens a Contentful response and returns the first Entry in the items array */
export function flattenEntryResponseSingle<T>(
  entries: EntryCollection<T>,
): Entry<T> | undefined {
  return flattenEntryResponse(entries)[0]
}

/** Flattens a Contentful response down to the `fields` prop */
export function flattenEntry<T>(entry: Entry<T>): T {
  return entry.fields
}

/**
 * Guard to assert that the given object is _not_ a Contentful link
 *
 * Useful as a top-level guard in Array.filter() operations (as opposed to using
 * the isLink() function supplied by the codegen utility, as TS can't infer a
 * type guard from a simple lambda.
 *
 * I.e. `funds.filter(fund => !isLink(fund))` doesn't work
 *
 * `funds.filter((fund): fund is IFund => !isLink(fund))` does work, but is
 * unnecessarily verbose
 *
 * @param obj
 */
export function isNotLink<T>(
  obj: ILink<'Entry'> | ILink<'Asset'> | T,
): obj is T {
  return !((obj as ILink<string>)?.sys?.type === 'Link')
}

/**
 * Splits markdown text into sections using an html comment separator.
 * This is used for interpolating more complicated components between sections of
 * markdown.
 *
 * @param markdown
 * @param separatorText
 * @returns
 */
export function splitMarkdown(
  markdown: string,
  separatorText = 'split',
): string[] {
  return markdown.split(
    new RegExp(`<!--+\\s*${separatorText}.*?-->\\s*\n+`, 'igm'),
  )
}

/**
 * Validates that the provided page has the expected number of markdown sections,
 * to avoid unnoticed changes in Contentful silently breaking production.
 *
 * @param Page
 * @param expectedSectionCount
 * @returns
 */
export function validateMarkdownSectionCount(
  Page: IPage,
  expectedSectionCount: number,
): void {
  const bodySections = splitMarkdown(Page.fields.body)

  if (bodySections.length !== expectedSectionCount) {
    throw new Error(
      `${Page.fields.slug} page expected ${expectedSectionCount} content sections; found ${bodySections.length}.`,
    )
  }
}

/**
 * Returns object if it is a Contentful entry, otherwise return undefined
 *
 * @param obj
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export function getEntry(obj: any): IEntry<any> | undefined {
  if (isEntry(obj)) return obj
}

/**
 * Returns the description of an Entry, which is the excerpt if it exists, else the body, truncated to 200 characters.
 *
 * @param Entry
 * @returns
 */
export function getDescription(
  Entry: IEntry<{ excerpt?: string; body?: string }>,
): string {
  return (
    Entry.fields.excerpt || removeMarkdown(Entry.fields.body || '')
  ).slice(0, 200)
}
